import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";
import { LineItemStatus, type Order, type OrderHistoryPayload } from "./types";
import { structureDate } from "@lib/utils/dateutils";
import { addPromotionNamesToOrders } from "./promotions";
import { addProductDetailsToOrders } from "./products";

export const getColorForOrderStatus = (orderStatus: LineItemStatus, isReturn?: boolean) => {
    // We return red.200 for OrderStatus "cancelled" or "returned" or return null otherwise.
    if (
        orderStatus === LineItemStatus.cancelledNotPickedUp ||
        orderStatus === LineItemStatus.cancelledOutOfStock ||
        orderStatus === LineItemStatus.Cancelled
    ) {
        return "red.200";
    }
    if (isReturn) {
        return "red.200";
    }
};

export const getFormatedDateForOrders = (date: string) => {
    const formattedDate = structureDate("YYYY-MM-DD", date.split("T")[0]);
    const year = formattedDate[0];
    const month = formattedDate[1];
    let day = formattedDate[2];
    if (day.startsWith("0")) {
        day = day.slice(1);
    }
    const monthNames = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
    ];
    const daySuffix = (day: string) => {
        const lastDigit = day[day.length - 1];
        if (day.length > 1 && day.startsWith("1")) {
            return "th";
        }
        switch (lastDigit) {
            case "1":
                return "st";
            case "2":
                return "nd";
            case "3":
                return "rd";
            default:
                return "th";
        }
    };
    const dayWithSuffix = day + daySuffix(day);
    return `${dayWithSuffix} ${monthNames[parseInt(month) - 1]} ${year}`;
};

export const createOrdersPayload = async (req, headers): Promise<OrderHistoryPayload> => {
    const { email, orderId, pageOffset, pageSize, includeChanges = true } = req.body;

    if (!isValidOrdersRequest(email, orderId, headers)) {
        return null;
    }

    return {
        locale: req.body.locale,
        market: req.body.market,
        pageOffset: pageOffset || 0,
        pageSize: pageSize || 10,
        includeChanges,
        ...(email && { email }),
        ...(orderId && { orderId }),
    };
};

export const isValidOrdersRequest = (email: string, orderId: string, headers): boolean => {
    if ((!email || !orderId) && !headers?.["x-ucid"]) {
        Logger.error(ServiceType.NEXT_API, "Request to get Orders failed because of no ucid", {
            tag: LogTag.ORDER_HISTORY,
            message: "The user is not logged in and no UCID is provided.",
        });
        return false;
    }

    if (!headers?.["x-heapSessionId"]) {
        Logger.error(
            ServiceType.NEXT_API,
            "Request to get Orders failed because of no heapsessionId",
            {
                tag: LogTag.ORDER_HISTORY,
                message: "The user does not have a Heap session ID.",
            }
        );
        return false;
    }

    return true;
};

export const addDetailsToOrders = async (
    orders: Order[],
    locale: string
): Promise<Order[] | null> => {
    if (!orders || orders.length === 0) {
        return null;
    }
    const ordersWithPromotions = await addPromotionNamesToOrders(orders, locale);
    const ordersWithProductDetails = await addProductDetailsToOrders(ordersWithPromotions, locale);
    if (!ordersWithProductDetails) return null;
    return ordersWithProductDetails;
};
